<template>
   <div class="pb-1" data-cy="kit-registration-v1">
      <div v-if="!state.isValid" class="portal-input mb-6 rounded-md">
         <TextBox
            id="kit-code"
            v-model.trim="state.code"
            input-classes="tracking-widest"
            :label="$t(`survey.blocks.kit_registration.enter_code`)"
            :hasError="showErrors && ($v.code.$error || state.kitValidationErrorMessage.length > 0)"
            :errorHelper="codeHelperData"
            :disabled="state.isValidating || state.isValid"
            @change="processInputs"
            @keyup.enter="validateKit"
         />
      </div>

      <LoadingButton
         v-show="!state.isValid"
         data-cy="kit-validation-btn"
         roundedEdges="md"
         :isLoading="state.isValidating"
         :text="$t(`survey.blocks.kit_registration.verify_code`)"
         @click="validateKit"
      />

      <div data-cy="kit-validation-status">
         <div v-if="state.isValid">
            <div class="flex items-center text-success-fg">
               <IconTickCircle class="mr-2 size-4 fill-success-fg" />
               {{ $t(`survey.blocks.kit_registration.code_verified`) }}
            </div>
            <div class="text-3xl leading-10 tracking-widest">
               {{ state.code }}
            </div>
            <DefaultButton
               v-if="!state.kitAlreadyValidatedAndAssigned"
               class="mt-6"
               data-cy="reset-button"
               style-type="text-only"
               @click="resetBlock"
            >
               {{ $t(`survey.blocks.kit_registration.reset_code`) }}
            </DefaultButton>
         </div>
      </div>
   </div>
</template>

<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";
import { useI18n } from "@/vue-i18n/vue-i18n";
import KitsApi from "@/data/api-layer/kitsApi";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";
import type { AnswerResp } from "@/data/api-layer/answersApi";

export interface KitRegistrationAnswerData {
   kitSerialNumber: string;
}

interface States {
   code: string;
   isValidating: boolean;
   isValid: boolean | null;
   showValidationErrors: boolean;
   kitValidationErrorMessage: string;
   kitAlreadyValidatedAndAssigned: boolean;
}

interface Props {
   block: FlowselBlock; // we could use KitRegBlock but it would be the same as FlowselBlock
   userBlockAnswer: AnswerResp | null;
   showBlockErrors: boolean;
}

// declarations
// -----------------------------------------
const { t: $t } = useI18n();
const emit = defineEmits(["next-displayed", "update-valid", "update-answer"]);
const props = defineProps<Props>();

const state = reactive<States>({
   code: props.userBlockAnswer?.data?.kitSerialNumber || "",
   isValidating: false,
   isValid: props.userBlockAnswer?.data?.kitSerialNumber ? true : null,
   showValidationErrors: false,
   kitValidationErrorMessage: "",
   kitAlreadyValidatedAndAssigned: !!props.userBlockAnswer?.metadata?.registeredAt,
});

// Validation
// -----------------------------------------
const rules = {
   code: {
      required: helpers.withMessage($t("validation.field_required"), required),
   },
};

const $v = useVuelidate(rules, state);

// computed
//-----------------------------------------------
const codeHelperData = computed(() => {
   if ($v.value.code.$errors.length > 0) {
      return {
         text: $v.value.code.$errors[0].$message as string,
         code: $v.value.code.$errors[0].$validator,
      };
   } else if (state.isValid === false) {
      return {
         text: state.kitValidationErrorMessage,
         code: "kit-validation",
      };
   } else {
      return null;
   }
});

const showErrors = computed(() => {
   return props.showBlockErrors || state.showValidationErrors;
});

//  methods
// -----------------------------------------
function resetBlock() {
   state.isValid = null;
   state.code = "";

   processInputs();
}

async function validateKit() {
   processInputs();

   if ($v.value.$error || $v.value.$invalid) {
      state.showValidationErrors = true;
      return;
   }

   state.showValidationErrors = false;
   state.isValidating = true;

   try {
      await KitsApi.getRegistrationStatus(state.code);
      const answerToSubmit = state.code ? { kit_serial_number: state.code } : null;

      emit("next-displayed", true);
      emit("update-valid", true);
      emit("update-answer", answerToSubmit);

      state.isValid = true;
   } catch (error: any) {
      /**
       * 404 - kit doesn't exist in database
       * 400 - kit is already assigned to another participant (this is not made public for privacy reasons)
       * generic error message is used for 400 and any other unexpected error
       */
      switch (error.response?.status) {
         case 404:
            state.kitValidationErrorMessage = $t("validation.kit_registration.kit_not_recognised");
            break;
         case 400:
         default:
            console.error("Error validating kit:", error);
            state.kitValidationErrorMessage = $t("validation.kit_registration.generic_error");
      }
      state.isValid = false;
      state.showValidationErrors = true;
   } finally {
      state.isValidating = false;
   }
}

/**
 * On input, check if the input is valid
 */
function processInputs() {
   if (!$v.value.$dirty) $v.value.$touch();

   // inputs have changed, so clear out this error
   state.kitValidationErrorMessage = "";

   if ($v.value.$error || $v.value.$invalid || !state.isValid) {
      // an input is invalid or has errors
      emit("update-valid", false);
      emit("next-displayed", false);

      // show errors if code validation has been attempted
      // this allows for validation to be visible if kit came back as invalid
      // but user still hasn't interacted with the next button so showBlockErrors is still false
      if (state.isValid !== null) state.showValidationErrors = true;
   } else {
      // all inputs valid
      // block validation is handled by the kit verification
      state.showValidationErrors = false;
   }
}
/**
 * if kit is already assigned (i.e. user has gone back to block)
 * it can't be changed and is automatically valid
 */
if (!state.kitAlreadyValidatedAndAssigned) {
   processInputs();
} else {
   emit("next-displayed", true);
   emit("update-valid", true);
}
</script>

<style lang="scss" scoped></style>
